import { DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, skip, take } from 'rxjs/operators';

import { TenantDto, TenantService } from '@stores/tenants';

// A full page must overflow the lists using it: the next page is loaded on scroll.
const PAGE_SIZE = 20;
// How close (px) to the end of the list scrolling gets before the next page is requested.
const LOAD_MORE_THRESHOLD_PX = 48;
const SEARCH_DEBOUNCE_MS = 300;

export interface TenantPaginationOptions {
  includeSubscriptions?: boolean;
}

/**
 * Tenant list backed by the paginated tenants API: `search` is applied server-side
 * (debounced) and the next page is appended when a list scrolls near its end.
 * Nothing is fetched until `reload()` is called.
 */
export class TenantPagination {
  private readonly tenantService = inject(TenantService);

  readonly search = signal('');
  readonly tenants = signal<readonly TenantDto[]>([]);
  readonly loading = signal(false);
  private nextPage = 0;
  private hasMore = true;
  private request: Subscription | null = null;

  constructor(private readonly options: TenantPaginationOptions = {}) {
    toObservable(this.search)
      .pipe(
        map((q) => q.trim()),
        distinctUntilChanged(),
        skip(1),
        debounceTime(SEARCH_DEBOUNCE_MS),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.reload());

    inject(DestroyRef).onDestroy(() => this.request?.unsubscribe());
  }

  /** Refetches from the first page, dropping the pages loaded so far. */
  reload(): void {
    this.load(0);
  }

  /** Appends the next page; a no-op while a page is loading or once all are loaded. */
  loadMore(): void {
    if (this.loading() || !this.hasMore) return;
    this.load(this.nextPage);
  }

  onListScroll(list: HTMLElement): void {
    if (list.scrollHeight - list.scrollTop - list.clientHeight <= LOAD_MORE_THRESHOLD_PX) {
      this.loadMore();
    }
  }

  private load(page: number): void {
    // A reload supersedes an in-flight request for a later page, whose offset would be stale.
    this.request?.unsubscribe();
    this.loading.set(true);
    this.request = this.tenantService
      .getTenantsPage(page, PAGE_SIZE, this.search().trim(), this.options.includeSubscriptions)
      .pipe(take(1))
      .subscribe({
        next: ({ tenants: fetched, totalElements }) => {
          // Tenants created elsewhere shift offsets between pages; skip ones already listed.
          const loaded = page === 0 ? [] : this.tenants();
          const loadedIds = new Set(loaded.map((t) => t.tenantUuid));
          this.tenants.set([...loaded, ...fetched.filter((t) => !loadedIds.has(t.tenantUuid))]);
          this.nextPage = page + 1;
          this.hasMore = (page + 1) * PAGE_SIZE < totalElements;
          this.loading.set(false);
        },
        error: () => {
          this.hasMore = false;
          this.loading.set(false);
        },
      });
  }
}
